import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { qk, useContextSources } from "../../api/queries";
import type { EnvBackend, EnvOs, EnvTemplate } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { JsonField } from "../../components/JsonField";

type Config = Record<string, unknown>;

interface Service {
  name: string;
  command: string;
  ready_port?: number;
}

export const BACKEND_LABEL: Record<EnvBackend, string> = {
  aws_ec2: "AWS EC2 instance",
  aws_ecs: "AWS ECS Fargate container",
  docker: "Docker container",
  local: "Local process",
};

const BACKEND_HELP: Record<EnvBackend, string> = {
  aws_ec2: "A VM per task from an AMI. Use it for Windows, or Linux that needs a full machine. Stopped while waiting on people.",
  aws_ecs: "A Linux container per task on Fargate. Starts fast; keeps running until the task is done (no persistent disk).",
  docker: "A container per task on the server host. For development.",
  local: "Runs the env-agent as a plain process on the server host, with no isolation. For development and tests only.",
};

// Fargate's supported CPU sizes (in CPU units) and their memory ranges (MiB).
const FARGATE_CPU: { units: number; label: string; memory: [number, number] }[] = [
  { units: 256, label: "0.25 vCPU", memory: [512, 2048] },
  { units: 512, label: "0.5 vCPU", memory: [1024, 4096] },
  { units: 1024, label: "1 vCPU", memory: [2048, 8192] },
  { units: 2048, label: "2 vCPU", memory: [4096, 16384] },
  { units: 4096, label: "4 vCPU", memory: [8192, 30720] },
  { units: 8192, label: "8 vCPU", memory: [16384, 61440] },
  { units: 16384, label: "16 vCPU", memory: [32768, 122880] },
];

const EC2_TYPES = ["m6i.xlarge", "m6i.2xlarge", "m7i-flex.xlarge", "t3.xlarge", "c6i.2xlarge"];

/** Config keys that only apply to some backends; dropped on save when they don't apply. */
const BACKEND_KEYS: Record<string, EnvBackend[]> = {
  ami_id: ["aws_ec2"],
  instance_type: ["aws_ec2"],
  warm_pool_size: ["aws_ec2"],
  image: ["aws_ecs", "docker"],
  cpu: ["aws_ecs", "docker"],
  memory: ["aws_ecs", "docker"],
  ephemeral_storage_gib: ["aws_ecs"],
  fargate_spot: ["aws_ecs"],
  egress_cidrs: ["aws_ec2", "aws_ecs"],
  idle_stop_minutes: ["aws_ec2", "docker", "local"], // ECS tasks can't stop and resume
};

const NEW_CONFIG: Config = {
  secret_env: ["ANTHROPIC_API_KEY"],
  idle_stop_minutes: 15,
  retention_hours: 4,
  budget: { max_run_minutes: 120, max_run_cost_usd: 20 },
};

const isEmpty = (v: unknown) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0);

const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? String(v) : "");
const toNum = (s: string) => (s.trim() === "" || !Number.isFinite(Number(s)) ? undefined : Number(s));
const strList = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const splitList = (s: string) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

function prune(config: Config, backend: EnvBackend): Config {
  const out: Config = {};
  for (const [k, v] of Object.entries(config)) {
    if (BACKEND_KEYS[k] && !BACKEND_KEYS[k].includes(backend)) continue;
    if (isEmpty(v)) continue;
    out[k] = v;
  }
  return out;
}

export function EnvTemplateForm({ pid, initial, onDone }: { pid: string; initial?: EnvTemplate; onDone: () => void }) {
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const [name, setName] = useState(initial?.name ?? "");
  const [backend, setBackend] = useState<EnvBackend>(initial?.backend ?? "aws_ec2");
  const [os, setOs] = useState<EnvOs>(initial?.os ?? "windows");
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [config, setConfig] = useState<Config>(initial?.config ?? NEW_CONFIG);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonValid, setJsonValid] = useState(true);

  const secrets = useQuery({ queryKey: qk.secrets(pid), queryFn: () => api.secrets(pid) });
  const sources = useContextSources(pid);
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.envTemplates(pid) });

  const linuxOnly = backend === "aws_ecs" || backend === "docker";
  const effectiveOs: EnvOs = linuxOnly ? "linux" : os;

  const set = (key: string, value: unknown) =>
    setConfig((c) => {
      const next = { ...c };
      if (isEmpty(value)) delete next[key];
      else next[key] = value;
      return next;
    });
  const budget = (config.budget as Config | undefined) ?? {};
  const setBudget = (key: string, value: number | undefined) => set("budget", prune({ ...budget, [key]: value }, backend));

  const currentConfig = (): Config | null => {
    if (!jsonMode) return config;
    try {
      return JSON.parse(jsonText || "{}") as Config;
    } catch {
      return null;
    }
  };

  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const cfg = currentConfig();
      if (!cfg) throw new Error("Fix the JSON before saving.");
      const body = { name, backend, os: effectiveOs, is_default: isDefault, config: prune(cfg, backend) };
      return initial ? api.patchEnvTemplate(initial.id, body) : api.createEnvTemplate(pid, body);
    },
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });
  const del = useMutation({
    mutationFn: () => api.deleteEnvTemplate(initial!.id),
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });

  const toggleJson = () => {
    if (jsonMode) {
      const cfg = currentConfig();
      if (!cfg) return;
      setConfig(cfg);
      setJsonMode(false);
    } else {
      setJsonText(JSON.stringify(prune(config, backend), null, 2));
      setJsonValid(true);
      setJsonMode(true);
    }
  };

  const missing: string[] = [];
  if (!name.trim()) missing.push("a name");
  if (!jsonMode && backend === "aws_ec2" && !str(config.ami_id)) missing.push("an AMI id");
  if (!jsonMode && (backend === "aws_ecs" || backend === "docker") && !str(config.image)) missing.push("an image");

  return (
    <div className="edit-card form-stack tpl-form">
      <div className="field-row">
        <label className="field">
          <span className="field-label">Name</span>
          <input className="input" value={name} placeholder="e.g. Windows build" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Runs on</span>
          <select className="input" value={backend} onChange={(e) => setBackend(e.target.value as EnvBackend)}>
            {(Object.keys(BACKEND_LABEL) as EnvBackend[]).map((b) => (
              <option key={b} value={b}>
                {BACKEND_LABEL[b]}
              </option>
            ))}
          </select>
        </label>
        <label className="field field-narrow">
          <span className="field-label">Operating system</span>
          <select
            className="input"
            value={effectiveOs}
            disabled={linuxOnly}
            onChange={(e) => setOs(e.target.value as EnvOs)}
          >
            <option value="windows">Windows</option>
            <option value="linux">Linux</option>
          </select>
        </label>
      </div>
      <p className="muted small tpl-help">{BACKEND_HELP[backend]}</p>
      <label className="checkbox">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Default template
        for new tasks
      </label>

      {jsonMode ? (
        <JsonField
          label="Config (JSON)"
          value={jsonText}
          rows={18}
          help="Settings that don't apply to the selected backend are dropped when you save."
          onChange={(t, ok) => {
            setJsonText(t);
            setJsonValid(ok);
          }}
        />
      ) : (
        <>
          {backend !== "local" && (
            <Section title="Machine">
              {backend === "aws_ec2" && (
                <div className="field-row">
                  <TextField
                    label="AMI id"
                    value={str(config.ami_id)}
                    placeholder="ami-0123456789abcdef0"
                    help="From the Packer build (infra/packer)."
                    onChange={(v) => set("ami_id", v.trim())}
                  />
                  <label className="field">
                    <span className="field-label">Instance type</span>
                    <input
                      className="input"
                      list="tpl-ec2-types"
                      value={str(config.instance_type)}
                      placeholder="m6i.xlarge"
                      onChange={(e) => set("instance_type", e.target.value.trim())}
                    />
                    <datalist id="tpl-ec2-types">
                      {EC2_TYPES.map((t) => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                    <span className="field-help muted">Default m6i.xlarge.</span>
                  </label>
                  <NumberField
                    label="Warm pool"
                    value={num(config.warm_pool_size)}
                    placeholder="0"
                    min={0}
                    help="Stopped, pre-booted instances so tasks start in seconds. Each costs ~$8/month in storage."
                    onChange={(v) => set("warm_pool_size", v)}
                  />
                </div>
              )}
              {(backend === "aws_ecs" || backend === "docker") && (
                <TextField
                  label="Image"
                  value={str(config.image)}
                  placeholder={
                    backend === "aws_ecs"
                      ? "123456789012.dkr.ecr.us-east-2.amazonaws.com/agent-task-runner-linux-env:latest"
                      : "agent-task-runner-linux-env:latest"
                  }
                  help={
                    backend === "aws_ecs"
                      ? "Push it with task env:linux:push. With :latest (or no tag), each new environment runs the most recently pushed image, pinned by digest and named in its log; a specific tag pins every environment to that build."
                      : "Must run env-agent as its entrypoint."
                  }
                  mono
                  onChange={(v) => set("image", v.trim())}
                />
              )}
              {backend === "aws_ecs" && <FargateSize config={config} set={set} />}
              {backend === "docker" && (
                <div className="field-row">
                  <NumberField
                    label="CPU units"
                    value={num(config.cpu)}
                    placeholder="no limit"
                    min={128}
                    step={128}
                    help="1024 = 1 CPU."
                    onChange={(v) => set("cpu", v)}
                  />
                  <NumberField
                    label="Memory (MiB)"
                    value={num(config.memory)}
                    placeholder="no limit"
                    min={256}
                    step={256}
                    onChange={(v) => set("memory", v)}
                  />
                </div>
              )}
            </Section>
          )}

          <Section title="Setup">
            <label className="field">
              <span className="field-label">Setup script</span>
              <textarea
                className="input input-mono"
                rows={3}
                spellCheck={false}
                value={str(config.setup_script)}
                placeholder={effectiveOs === "windows" ? "dotnet restore" : "npm ci"}
                onChange={(e) => set("setup_script", e.target.value)}
              />
              <span className="field-help muted">
                Runs in the repository after checkout, in {effectiveOs === "windows" ? "PowerShell" : "bash"}. A failure
                stops the task with the script's error.
              </span>
            </label>
            <ServicesField
              value={(config.services as Service[] | undefined) ?? []}
              windows={effectiveOs === "windows"}
              onChange={(v) => set("services", v)}
            />
          </Section>

          <Section title="Access">
            <SecretsField
              selected={strList(config.secret_env)}
              available={(secrets.data ?? []).map((s) => s.name)}
              onChange={(v) => set("secret_env", v)}
            />
            <div className="field">
              <span className="field-label">Context sources</span>
              {sources.data && sources.data.length > 0 ? (
                <div className="checkbox-list">
                  {sources.data.map((s) => {
                    const ids = strList(config.context_source_ids);
                    return (
                      <label key={s.id} className="checkbox">
                        <input
                          type="checkbox"
                          checked={ids.includes(s.id)}
                          onChange={(e) =>
                            set("context_source_ids", e.target.checked ? [...ids, s.id] : ids.filter((x) => x !== s.id))
                          }
                        />
                        {s.name} <span className="muted small">{s.kind}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <span className="muted small">None yet. Add them under Context sources.</span>
              )}
            </div>
            <div className="field-row">
              <ListField
                label="Preview ports"
                value={((config.preview_ports as number[] | undefined) ?? []).join(", ")}
                placeholder="8080"
                help="Web ports inside the environment that reviewers can open from the task."
                onChange={(s) => set("preview_ports", splitList(s).map(Number).filter((n) => Number.isInteger(n) && n > 0 && n < 65536))}
              />
              {(backend === "aws_ec2" || backend === "aws_ecs") && (
                <ListField
                  label="Allowed outbound (CIDRs)"
                  value={strList(config.egress_cidrs).join(", ")}
                  placeholder="0.0.0.0/0 (anywhere)"
                  help="Restricting this also blocks model APIs and GitHub unless their ranges are listed."
                  onChange={(s) => set("egress_cidrs", splitList(s))}
                />
              )}
            </div>
          </Section>

          <Section title="Lifecycle and limits">
            <div className="field-row">
              {BACKEND_KEYS.idle_stop_minutes.includes(backend) && (
                <NumberField
                  label="Stop when idle (minutes)"
                  value={num(config.idle_stop_minutes)}
                  placeholder="30"
                  min={1}
                  help="While the task waits on people. The disk is kept."
                  onChange={(v) => set("idle_stop_minutes", v)}
                />
              )}
              <NumberField
                label="Keep after done (hours)"
                value={num(config.retention_hours)}
                placeholder="24"
                min={0}
                help="Then the environment is deleted."
                onChange={(v) => set("retention_hours", v)}
              />
              <NumberField
                label="Max run time (minutes)"
                value={num(budget.max_run_minutes)}
                placeholder="180"
                min={1}
                onChange={(v) => setBudget("max_run_minutes", v)}
              />
              <NumberField
                label="Max run cost (USD)"
                value={num(budget.max_run_cost_usd)}
                placeholder="no limit"
                min={0}
                step={1}
                onChange={(v) => setBudget("max_run_cost_usd", v)}
              />
            </div>
            <span className="field-help muted">Runs over a limit are stopped and the task is flagged; the environment is kept.</span>
          </Section>

          <Section title="Agent">
            <div className="field-row">
              <TextField
                label="Model"
                value={str(config.model)}
                placeholder="CLI default, e.g. claude-opus-5-5"
                help="Passed to the agent CLI as --model / -m. Prefer a full model ID (claude-opus-5-5) over an alias (opus): what an alias means depends on the CLI version in the environment image."
                onChange={(v) => set("model", v.trim())}
              />
              <TextField
                label="Server address for environments"
                value={str(config.agent_url)}
                placeholder="server default"
                help="Advanced: overrides the WebSocket URL env-agents dial, e.g. ws://host.docker.internal:8080/agent/ws."
                mono
                onChange={(v) => set("agent_url", v.trim())}
              />
            </div>
          </Section>
        </>
      )}

      {save.error && <div className="error-box">{save.error.message}</div>}
      <div className="row-actions">
        {initial && (
          <button
            className="btn btn-ghost-danger"
            onClick={async () => {
              if (await confirm({ title: `Delete template ${initial.name}?`, danger: true, confirmLabel: "Delete" }))
                del.mutate();
            }}
          >
            Delete
          </button>
        )}
        <button className="btn btn-sm" onClick={toggleJson} disabled={jsonMode && !jsonValid}>
          {jsonMode ? "Edit as form" : "Edit as JSON"}
        </button>
        <span className="spacer" />
        {missing.length > 0 && <span className="muted small">Needs {missing.join(" and ")}</span>}
        <button className="btn" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={missing.length > 0 || (jsonMode && !jsonValid) || save.isPending}
          onClick={() => save.mutate()}
        >
          {initial ? "Save" : "Create"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="tpl-section">
      <legend>{title}</legend>
      <div className="form-stack">{children}</div>
    </fieldset>
  );
}

function TextField(props: {
  label: string;
  value: string;
  placeholder?: string;
  help?: ReactNode;
  mono?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <label className="field">
      <span className="field-label">{props.label}</span>
      <input
        className={props.mono ? "input input-mono" : "input"}
        value={props.value}
        placeholder={props.placeholder}
        spellCheck={false}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.help && <span className="field-help muted">{props.help}</span>}
    </label>
  );
}

/** A number input that keeps what the user types and reports a number (or undefined when empty). */
function NumberField(props: {
  label: string;
  value: string;
  placeholder?: string;
  help?: ReactNode;
  min?: number;
  step?: number;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <label className="field">
      <span className="field-label">{props.label}</span>
      <input
        className="input"
        type="number"
        inputMode="numeric"
        value={props.value}
        placeholder={props.placeholder}
        min={props.min}
        step={props.step}
        onChange={(e) => props.onChange(toNum(e.target.value))}
      />
      {props.help && <span className="field-help muted">{props.help}</span>}
    </label>
  );
}

/** Comma- or space-separated list, edited as text and committed on blur. */
function ListField(props: {
  label: string;
  value: string;
  placeholder?: string;
  help?: ReactNode;
  onChange: (text: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <label className="field">
      <span className="field-label">{props.label}</span>
      <input
        className="input input-mono"
        value={draft ?? props.value}
        placeholder={props.placeholder}
        spellCheck={false}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null) props.onChange(draft);
          setDraft(null);
        }}
      />
      {props.help && <span className="field-help muted">{props.help}</span>}
    </label>
  );
}

function FargateSize({ config, set }: { config: Config; set: (k: string, v: unknown) => void }) {
  const cpu = typeof config.cpu === "number" ? config.cpu : 2048;
  const size = FARGATE_CPU.find((s) => s.units === cpu);
  const memory = typeof config.memory === "number" ? config.memory : 8192;
  const memOk = !size || (memory >= size.memory[0] && memory <= size.memory[1]);
  return (
    <>
      <div className="field-row">
        <label className="field">
          <span className="field-label">CPU</span>
          <select
            className="input"
            value={size ? String(cpu) : ""}
            onChange={(e) => {
              const units = Number(e.target.value);
              const s = FARGATE_CPU.find((x) => x.units === units)!;
              set("cpu", units);
              if (memory < s.memory[0] || memory > s.memory[1]) set("memory", s.memory[0]);
            }}
          >
            {!size && <option value="">{cpu} units (custom)</option>}
            {FARGATE_CPU.map((s) => (
              <option key={s.units} value={s.units}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <NumberField
          label="Memory (MiB)"
          value={String(memory)}
          min={size?.memory[0] ?? 512}
          step={1024}
          help={size ? `${size.memory[0]}–${size.memory[1]} MiB for ${size.label}.` : undefined}
          onChange={(v) => set("memory", v)}
        />
        <NumberField
          label="Disk (GiB)"
          value={num(config.ephemeral_storage_gib)}
          placeholder="20"
          min={21}
          help="21–200 GiB; leave empty for Fargate's default 20 GiB."
          onChange={(v) => set("ephemeral_storage_gib", v)}
        />
      </div>
      {!memOk && <span className="field-help tpl-warn">That memory size isn't valid for this CPU on Fargate.</span>}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={config.fargate_spot === true}
          onChange={(e) => set("fargate_spot", e.target.checked ? true : undefined)}
        />
        Use Fargate Spot <span className="muted small">(about 70% cheaper; AWS may stop the container, which fails the run)</span>
      </label>
    </>
  );
}

function ServicesField({ value, windows, onChange }: { value: Service[]; windows: boolean; onChange: (v: Service[]) => void }) {
  const update = (i: number, patch: Partial<Service>) => onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="field">
      <span className="field-label">Services</span>
      <span className="field-help muted">
        Started before the setup script and again after restarts, e.g. device emulators. The task waits for each ready
        port.
      </span>
      {value.length > 0 && (
        <div className="tpl-services">
          {value.map((s, i) => (
            <div key={i} className="tpl-service">
              <input
                className="input"
                aria-label="Service name"
                placeholder="name"
                value={s.name}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              <input
                className="input input-mono"
                aria-label="Command"
                placeholder={windows ? "C:\\emulators\\lis.exe --com COM11" : "node tools/mock-api.js"}
                value={s.command}
                spellCheck={false}
                onChange={(e) => update(i, { command: e.target.value })}
              />
              <input
                className="input"
                aria-label="Ready port"
                type="number"
                placeholder="port"
                value={s.ready_port ?? ""}
                onChange={(e) => update(i, { ready_port: toNum(e.target.value) })}
              />
              <button className="btn btn-sm" aria-label={`Remove service ${s.name || i + 1}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <div>
        <button className="btn btn-sm" onClick={() => onChange([...value, { name: "", command: "" }])}>
          Add service
        </button>
      </div>
    </div>
  );
}

function SecretsField({ selected, available, onChange }: { selected: string[]; available: string[]; onChange: (v: string[]) => void }) {
  const names = Array.from(new Set([...available, ...selected])).sort();
  return (
    <div className="field">
      <span className="field-label">Secrets passed to the agent</span>
      {names.length > 0 ? (
        <div className="checkbox-list">
          {names.map((n) => (
            <label key={n} className="checkbox">
              <input
                type="checkbox"
                checked={selected.includes(n)}
                onChange={(e) => onChange(e.target.checked ? [...selected, n] : selected.filter((x) => x !== n))}
              />
              <code>{n}</code>
              {!available.includes(n) && <span className="tpl-warn small">not set</span>}
            </label>
          ))}
        </div>
      ) : (
        <span className="muted small">No secrets yet. Add them under Secrets.</span>
      )}
      <span className="field-help muted">
        Set as environment variables for the agent CLI, e.g. ANTHROPIC_API_KEY or OPENAI_API_KEY. GITHUB_TOKEN is used by
        the runner for git and doesn't need to be listed.
      </span>
    </div>
  );
}
